import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Devices as DevicesIcon,
  Battery3Bar as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { collection, doc, updateDoc, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Device, generateQrCodeNumber, DEFAULT_COORDINATE_DELTAS, convertTimestamp } from '../../types/firebase';

const DeviceCard = styled(Card)(({ theme }) => {
  const { colors } = useTheme();
  return {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    marginBottom: '12px',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(34, 211, 238, 0.05)',
      borderColor: colors.Gradient,
      transform: 'translateY(-2px)',
    },
  };
});

const StatusChip = styled(Chip)<{ status: 'online' | 'offline' | 'maintenance' | 'error' }>(({ status, theme }) => {
  const { colors } = useTheme();
  return {
    backgroundColor:
      status === 'online' ? colors.Success :
        status === 'offline' ? colors.Error :
          status === 'maintenance' ? colors.Warning :
            colors.Error,
    color: colors.Light_Shadow,
    fontWeight: 'bold',
  };
});

const ActionButton = styled(IconButton)(({ theme }) => {
  const { colors } = useTheme();
  return {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: colors.TextSecondary,
    margin: '0 4px',
    '&:hover': {
      backgroundColor: colors.Gradient,
      color: colors.Light_Shadow,
    },
  };
});

// Legacy interface removed - using mobile app compatible Device interface from firebase types

interface DeviceManagementProps {
  siteId: string;
  siteName: string;
  devices: Device[];
  onDevicesUpdate: (devices: Device[]) => void;
}

interface AddDeviceModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (deviceData: any) => Promise<void>;
  editingDevice?: Device | null;
}

const AddDeviceModal: React.FC<AddDeviceModalProps> = ({
  open,
  onClose,
  onSave,
  editingDevice
}) => {
  const { colors } = useTheme();
  const [formData, setFormData] = useState({
    modelName: '',
    deviceType: '',
    modelNumber: '',
    specificLocation: '',
    coordinates: {
      latitude: 0,
      longitude: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingDevice) {
      setFormData({
        modelName: editingDevice.modelName || '',
        deviceType: editingDevice.deviceType || '',
        modelNumber: editingDevice.modelNumber || '',
        specificLocation: editingDevice.specificLocation || '',
        coordinates: {
          latitude: editingDevice.coordinates?.latitude || 0,
          longitude: editingDevice.coordinates?.longitude || 0,
        },
      });
    } else {
      setFormData({
        modelName: '',
        deviceType: '',
        modelNumber: '',
        specificLocation: '',
        coordinates: {
          latitude: 0,
          longitude: 0,
        },
      });
    }
  }, [editingDevice, open]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev] as any,
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };


  const handleSubmit = async () => {
    if (!formData.modelName || !formData.deviceType || !formData.modelNumber) {
      setError('Device type, model name, and model number are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create mobile app compatible device structure
      const deviceData: Partial<Device> = {
        qrCodeNumber: editingDevice?.qrCodeNumber || generateQrCodeNumber(),
        deviceType: formData.deviceType,
        modelNumber: formData.modelNumber,
        modelName: formData.modelName,
        specificLocation: formData.specificLocation,
        coordinates: {
          latitude: formData.coordinates.latitude,
          longitude: formData.coordinates.longitude,
          ...DEFAULT_COORDINATE_DELTAS,
        },
        qrCodeUrl: editingDevice?.qrCodeUrl || '', // Placeholder - QR generation will be implemented later
        createdAt: editingDevice?.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      await onSave(deviceData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save device');
    } finally {
      setLoading(false);
    }
  };

  const paperSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  };
  const titleSx = {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase' as const,
    padding: '20px 24px',
    borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
    backgroundColor: 'transparent',
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={titleSx}>
        {editingDevice ? 'Edit Device' : 'Add New Device'}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}

        <Box sx={{ marginTop: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Device Model</InputLabel>
              <Select
                value={formData.modelName}
                onChange={(e) => handleInputChange('modelName', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="SkimBuster V1">SkimBuster V1</MenuItem>
                <MenuItem value="SkimBuster V2">SkimBuster V2</MenuItem>
                <MenuItem value="SkimBuster Pro">SkimBuster Pro</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={formData.deviceType}
                onChange={(e) => handleInputChange('deviceType', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="Card Reader Scanner">Card Reader Scanner</MenuItem>
                <MenuItem value="ATM Scanner">ATM Scanner</MenuItem>
                <MenuItem value="POS Scanner">POS Scanner</MenuItem>
                <MenuItem value="Multi-Purpose Scanner">Multi-Purpose Scanner</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="Model Number *"
            value={formData.modelNumber}
            onChange={(e) => handleInputChange('modelNumber', e.target.value)}
            disabled={loading}
            placeholder="e.g., iCT250, SelfServ 80"
            sx={{ marginBottom: 2 }}
          />

          <TextField
            fullWidth
            label="Specific Location (Optional)"
            value={formData.specificLocation}
            onChange={(e) => handleInputChange('specificLocation', e.target.value)}
            disabled={loading}
            placeholder="e.g., Pump 1, Row A, Main ATM - Front Entrance"
            sx={{ marginBottom: 2 }}
          />

          <Typography variant="h6" color={colors.TextPrimary} sx={{ marginBottom: 2 }}>
            Location Coordinates (Optional)
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={formData.coordinates.latitude}
              onChange={(e) => handleInputChange('coordinates.latitude', parseFloat(e.target.value) || 0)}
              disabled={loading}
              inputProps={{ step: 'any' }}
            />

            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={formData.coordinates.longitude}
              onChange={(e) => handleInputChange('coordinates.longitude', parseFloat(e.target.value) || 0)}
              disabled={loading}
              inputProps={{ step: 'any' }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="outlined"
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: colors.TextMuted, background: 'rgba(255,255,255,0.05)' },
          }}
        >
          {loading ? 'Saving...' : 'Save Device'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeviceManagement: React.FC<DeviceManagementProps> = ({
  siteId,
  siteName,
  devices,
  onDevicesUpdate
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [error, setError] = useState('');

  const { userProfile } = useAuth();
  const { colors } = useTheme();

  const getDeviceStatus = (device: Device): 'online' | 'offline' | 'maintenance' | 'error' => {
    if (!device.lastSeen) return 'offline';

    const now = new Date();
    const lastSeen = convertTimestamp(device.lastSeen);
    const timeDiff = now.getTime() - lastSeen.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);

    if ((device.batteryLevel ?? 0) < 10) return 'error';
    if (hoursDiff < 1) return 'online';
    if (hoursDiff < 24) return 'maintenance';
    return 'offline';
  };

  const getBatteryColor = (level: number | undefined) => {
    const batteryLevel = level ?? 0;
    if (batteryLevel > 50) return colors.Success;
    if (batteryLevel > 20) return colors.Warning;
    return colors.Error;
  };

  const getSignalColor = (strength: number | undefined) => {
    const signalStrength = strength ?? 0;
    if (signalStrength > 70) return colors.Success;
    if (signalStrength > 30) return colors.Warning;
    return colors.Error;
  };

  const handleAddDevice = async (deviceData: any) => {
    try {
      const deviceRef = collection(db, `sites/${siteId}/devices`);
      const docRef = await addDoc(deviceRef, deviceData);

      const newDevice = {
        id: docRef.id,
        ...deviceData,
      };

      onDevicesUpdate([...devices, newDevice]);
    } catch (err: any) {
      setError('Failed to add device');
      throw err;
    }
  };

  const handleEditDevice = async (deviceData: any) => {
    if (!editingDevice?.id || !siteId) return;

    try {
      const deviceRef = doc(db, 'sites', siteId, 'devices', editingDevice.id);
      await updateDoc(deviceRef, deviceData);

      const updatedDevices = devices.map(device =>
        device.id === editingDevice.id ? { ...device, ...deviceData } : device
      );

      onDevicesUpdate(updatedDevices);
      setEditingDevice(null);
    } catch (err: any) {
      setError('Failed to update device');
      throw err;
    }
  };

  const handleDeleteDevice = async () => {
    if (!deviceToDelete?.id || !siteId) return;

    try {
      const deviceRef = doc(db, 'sites', siteId, 'devices', deviceToDelete.id);
      await deleteDoc(deviceRef);

      const updatedDevices = devices.filter(device => device.id !== deviceToDelete.id);
      onDevicesUpdate(updatedDevices);

      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch (err: any) {
      setError('Failed to delete device');
    }
  };

  const openEditModal = (device: Device) => {
    setEditingDevice(device);
    setAddModalOpen(true);
  };

  const openDeleteDialog = (device: Device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const closeModal = () => {
    setAddModalOpen(false);
    setEditingDevice(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Typography variant="h6" color={colors.TextPrimary}>
          Device Management - {siteName}
        </Typography>
        {userProfile?.role === 'Admin' && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setAddModalOpen(true)}
            sx={{
              backgroundColor: colors.Gradient,
              '&:hover': { backgroundColor: colors.Gradient_2 }
            }}
          >
            Add Device
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {devices.length === 0 ? (
        <Box textAlign="center" padding={4}>
          <DevicesIcon sx={{ fontSize: 64, color: colors.TextMuted, marginBottom: 2 }} />
          <Typography color={colors.TextSecondary} variant="h6" gutterBottom>
            No devices assigned
          </Typography>
          <Typography color={colors.TextMuted}>
            {userProfile?.role === 'Admin'
              ? 'Click "Add Device" to assign your first device to this site'
              : 'Contact your administrator to assign devices to this site'
            }
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 2 }}>
          {devices.map((device) => (
            <DeviceCard key={device.id}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
                  <Box>
                    <Typography
                      variant="h6"
                      color={colors.TextPrimary}
                      gutterBottom
                      sx={{ fontWeight: 'bold' }}
                    >
                      {device.modelName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.TextSecondary}
                      gutterBottom
                    >
                      {device.deviceType}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.TextMuted}
                    >
                      Model: {device.modelNumber}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.TextMuted}
                    >
                      QR: #{device.qrCodeNumber}
                    </Typography>
                  </Box>

                  <StatusChip
                    label={getDeviceStatus(device)}
                    status={getDeviceStatus(device)}
                    size="small"
                  />
                </Box>

                {device.location?.description && (
                  <Box display="flex" alignItems="center" marginBottom={1}>
                    <LocationIcon sx={{ color: colors.TextMuted, fontSize: 16, marginRight: 1 }} />
                    <Typography variant="body2" color={colors.TextSecondary}>
                      {device.location.description}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                  <Box display="flex" alignItems="center">
                    <BatteryIcon sx={{ color: getBatteryColor(device.batteryLevel), fontSize: 18, marginRight: 0.5 }} />
                    <Typography variant="body2" color={colors.TextSecondary}>
                      {device.batteryLevel ?? 0}%
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center">
                    <SignalIcon sx={{ color: getSignalColor(device.signalStrength), fontSize: 18, marginRight: 0.5 }} />
                    <Typography variant="body2" color={colors.TextSecondary}>
                      {device.signalStrength ?? 0}%
                    </Typography>
                  </Box>

                  <Chip
                    label={`v${device.firmwareVersion}`}
                    size="small"
                    sx={{
                      backgroundColor: colors.Info,
                      color: colors.Light_Shadow,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>

                <Typography variant="body2" color={colors.TextMuted} marginBottom={2}>
                  Last seen: {device.lastSeen ? convertTimestamp(device.lastSeen).toLocaleString() : 'Never'}
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    icon={<ScheduleIcon />}
                    label={`${device.configuration?.scanInterval || 30}s scan`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: colors.TextMuted,
                      color: colors.TextSecondary,
                    }}
                  />

                  <Chip
                    label={`${device.configuration?.sensitivity || 'medium'} sensitivity`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: colors.TextMuted,
                      color: colors.TextSecondary,
                    }}
                  />
                </Box>

                {userProfile?.role === 'Admin' && (
                  <Box display="flex" justifyContent="flex-end" marginTop={2}>
                    <ActionButton size="small" onClick={() => openEditModal(device)} title="Edit Device">
                      <EditIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton
                      size="small"
                      onClick={() => openDeleteDialog(device)}
                      title="Delete Device"
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.Error,
                          color: colors.Light_Shadow,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  </Box>
                )}
              </CardContent>
            </DeviceCard>
          ))}
        </Box>
      )}

      {/* Add/Edit Device Modal */}
      <AddDeviceModal
        open={addModalOpen}
        onClose={closeModal}
        onSave={editingDevice ? handleEditDevice : handleAddDevice}
        editingDevice={editingDevice}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Delete Device
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Typography color={colors.TextPrimary}>
            Are you sure you want to delete "{deviceToDelete?.modelName}"
            (S/N: {deviceToDelete?.serialNumber})? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDevice}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(244, 67, 54, 0.5)',
              background: 'rgba(244, 67, 54, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(244, 67, 54, 0.3)', borderColor: 'rgba(244, 67, 54, 0.7)' },
            }}
          >
            Delete Device
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;
